import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { useQueryParamsEffect, useQueryParamTerm } from 'pricing-pages-ui/utils/urls/appQueryParamUtils';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { CMS, MARKETING } from 'self-service-api/constants/ProductLines';
import { getHighlightsForProductLine, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { OWNERSHIP_CHECK_FAILED } from '../../constants/ErrorCauses';
import { FaqsConfig } from '../../constants/faqs/FaqsConfig';
import { getMerchCardsToShow } from '../../constants/merchCards/MerchCardsToShow';
import Screens from '../../constants/routing/Screens';
import { ProductPageContext } from '../../context/ProductPageContext';
import { useAnchorLinkScroll } from '../../hooks/htmlElementHooks';
import { useBusinessObjectives } from '../../hooks/useBusinessObjectives';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { usePageParams } from '../../hooks/usePageParams';
import { useTrackRetail } from '../../hooks/useTrackRetail';
import { useAppSelector } from '../../reducers';
import { isUngatedForMarketingPlusSelector } from '../../selectors/GatingSelectors';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector, hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { navItemForProductLineSelector } from '../../selectors/RoutingSelectors';
import { useSSM0004ExperimentEffect } from '../../utils/experiments/ssm0004';
import BannerSection from '../banners/BannerSection';
import CodeSplitEnvWrapper from '../common/env/CodeSplitEnvWrapper';
import IntegrationsSection from '../common/Integrations';
import FailedLoadBanner from '../common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../common/loading/FallbackErrorBanner';
import PhoneSupport from '../common/PhoneSupport';
import PhoneSupportFooter from '../common/PhoneSupportFooter';
import ProductInfoContainer from '../common/ProductInfoContainer';
import ValuePropsSection from '../common/ValuePropsSection';
import CompareStatusFooter from '../compare/CompareStatusFooter';
import ErrorPage from '../error-page/ErrorPage';
import Faq from '../faqs/Faq';
import FooterMicrocopy from '../faqs/FooterMicrocopy';
import Question from '../faqs/Question';
import FeatureTable from '../feature-table/FeatureTable';
import HeaderLayout from '../layout/HeaderLayout';
import MarketingPlusPromoCard from '../marketing-plus/MarketingPlusPromoCard';
import HubTierInfo from '../merch-card/HubTierInfo';
import ProductPageSubtitle from './ProductPageSubtitle';
import ProductPageTitle, { ProductPageHeader } from './ProductPageTitle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ProductPage = ({
  productLine
}) => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle(productLine.toLowerCase());
  const hasPricingAndPurchasingSucceeded = useAppSelector(hasPricingAndPurchasingSucceededSelector);
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useSelector(hasOnlyOwnershipCheckFailedSelector);
  const isUngatedForMarketingPlus = useSelector(isUngatedForMarketingPlusSelector);
  const {
    tab
  } = usePageParams();
  const shouldDisplayMarketingPlusPromoCard = (productLine === CMS || productLine === MARKETING) && isUngatedForMarketingPlus;
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: Screens[productLine],
      tab
    });
  }, [productLine, tab]);
  const {
    isSSM0004Loading
  } = useSSM0004ExperimentEffect();
  const highlightedProducts = getHighlightsForProductLine(productLine);
  const productLevels = getMerchCardsToShow({
    tab
  });
  const filteredHighlightedProducts = highlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  useBusinessObjectives({
    highlightedProducts: filteredHighlightedProducts
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const navItem = useAppSelector(state => navItemForProductLineSelector(state, productLine));
  const faqsConfig = FaqsConfig[navItem];
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }
  const screen = productLine.toLowerCase();

  // Only true in-app to prevent from showing term toggle / monthly price for split second while experiment cohort is being assigned
  if (isSSM0004Loading) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [hasPricingAndPurchasingSucceeded && /*#__PURE__*/_jsx(NavMarker, {
      name: `${productLine}_PAGE`
    }), /*#__PURE__*/_jsx(HeaderLayout, {
      selectedNavItem: navItem,
      children: /*#__PURE__*/_jsx(ProductInfoContainer, {
        children: /*#__PURE__*/_jsxs(ProductPageContext.Provider, {
          value: {
            highlightedProducts: filteredHighlightedProducts
          },
          children: [/*#__PURE__*/_jsx(BannerSection, {
            productLine: productLine,
            screen: screen
          }), /*#__PURE__*/_jsx(FailedLoadBanner, {
            hasFailed: hasPricingAndPurchasingFailed
          }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(ProductPageHeader, {
            children: [/*#__PURE__*/_jsx(ProductPageTitle, {
              productLine: productLine
            }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
              productLine: productLine
            })]
          }), /*#__PURE__*/_jsx(HubTierInfo, {
            productLine: productLine,
            selectedTab: tab
          }), /*#__PURE__*/_jsx(FeatureTable, {
            productLine: productLine,
            highlightedProducts: filteredHighlightedProducts,
            screen: Screens[productLine],
            tab: tab
          }, `${productLine}-${tab}`), shouldDisplayMarketingPlusPromoCard && /*#__PURE__*/_jsx(MarketingPlusPromoCard, {}), /*#__PURE__*/_jsx(PhoneSupport, {
            phoneSourceId: `${productLine.toLowerCase()}-middle`,
            screen: Screens[productLine]
          }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/feature-modal/FeatureCardWrapper`),
            publicComponentProps: {
              productLine
            }
          }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
            publicComponentProps: {
              tab
            }
          }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/case-studies/CaseStudies`),
            publicComponentProps: {
              productLine,
              tab
            }
          }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsxs(Faq, {
            children: [faqsConfig.map(_ref => {
              let {
                  questionKey
                } = _ref,
                passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
              return /*#__PURE__*/_jsx(Question, Object.assign({
                questionKey: questionKey
              }, passthroughOptions), questionKey);
            }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
          }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
            phoneSourceId: `${productLine.toLowerCase()}-footer`,
            screen: Screens[productLine]
          })]
        })
      })
    }), /*#__PURE__*/_jsx(CompareStatusFooter, {})]
  });
};
export default ProductPage;